import * as React from 'react'
import Head from 'next/head'
import { NextSeo } from 'next-seo'
import {
	Badge,
	Card,
	Group,
	Image,
	SimpleGrid,
	Spoiler,
	Text,
	Title,
} from '@mantine/core'
import Link from 'next/link'

import * as Types from 'declarations'
import * as RedisUtil from 'lib/redis'
import { getUploadThumbPath } from 'lib/display'
import { getDefaultImage } from 'lib/helper'
import { withVersion } from 'lib/withVersion'

import PageTemplate from 'components/PageTemplate'

interface ArticlesPageProps {
	// Slim summary of Types.API.Article — the full objects (incl. ~25KB
	// draftJson/liveJson bodies) were inflating __NEXT_DATA__ to ~3.4MB
	articles: ArticleSummary[]
	version: Types.VERSION
}

export type ArticleSummary = Pick<Types.API.Article, 'title' | 'slug'> & {
	liveJson: Pick<
		Types.App.ArticleJSONData,
		'coverImage' | 'subtitle' | 'seoDescription' | 'tags' | 'isFeaturedArticle'
	>
}

const ArticleCard = ({ article }: { article: ArticleSummary }) => {
	return (
		<Link href={`/articles/${article.slug}`}>
			<Card withBorder radius="md" p="md">
				<Card.Section>
					{article.liveJson.coverImage && (
						<Image
							src={getUploadThumbPath(article.liveJson.coverImage, 'medium')}
							alt={article.title}
							height={225}
						/>
					)}
				</Card.Section>

				<Card.Section mt="md" p="md">
					<Title
						order={3}
						fz="lg"
						fw={500}
						// component={Link}
						// href={`/articles/${article.slug}`}
					>
						{article.title}
					</Title>
					{article?.liveJson?.subtitle && (
						<Text fz="md" c="dimmed" mt="xs">
							{article.liveJson.subtitle}
						</Text>
					)}
					<Text fz="sm" mt="md">
						{article.liveJson.seoDescription}
					</Text>
				</Card.Section>

				{(article?.liveJson?.tags ?? []).length > 0 && (
					<Card.Section p="md">
						<Spoiler maxHeight={80} showLabel="Show more" hideLabel="Hide">
							<Group gap={7}>
								{article.liveJson.tags.map((tag, i) => (
									<Badge key={i} radius="sm" variant="outline">
										{tag}
									</Badge>
								))}
							</Group>
						</Spoiler>
					</Card.Section>
				)}
			</Card>
		</Link>
	)
}

const ArticlesPage = ({ articles, version }: ArticlesPageProps) => {
	// Featured articles first, then everything else — all rendered server-side
	// so every article is reachable via a crawlable internal link.
	const displayArticles = React.useMemo(() => {
		const featured = articles.filter(
			(article) => article.liveJson.isFeaturedArticle,
		)
		const nonFeatured = articles.filter(
			(article) => !article.liveJson.isFeaturedArticle,
		)
		return [...featured, ...nonFeatured]
	}, [articles])

	const seo = {
		title: `Articles - ${
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks'
		}`,
		description:
			version === Types.VERSION.TREKKING
				? 'Explore trekking articles on Guided Trekking. Discover tips, guides, and stories to inspire and prepare you for your next trekking adventure.'
				: 'Explore mountaineering articles on Guided Peaks. Discover tips, guides, and stories to inspire and prepare you for your next climbing adventure.',
		canonical: `https://www.guided${
			version === Types.VERSION.TREKKING ? 'trekking' : 'peaks'
		}.com/articles`,
		images: [
			{
				url: getDefaultImage(version),
				type: 'image/jpeg',
			},
		],
		siteName:
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks',
	}

	return (
		<>
			<Head>
				<title>{seo.title}</title>
			</Head>
			<NextSeo
				title={seo.title}
				description={seo.description}
				canonical={seo.canonical}
				openGraph={{
					url: seo.canonical,
					title: seo.title,
					description: seo.description,
					images: seo.images,
					siteName: seo.siteName,
					type: 'website',
					locale: 'en_US',
				}}
			/>
			<PageTemplate>
				<Title order={1} mb="lg">
					Articles
				</Title>
				<SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
					{displayArticles.map((article, index) => (
						<ArticleCard key={index} article={article} />
					))}
				</SimpleGrid>
			</PageTemplate>
		</>
	)
}

export const getServerSideProps = withVersion(
	async (context: { version: Types.VERSION }) => {
		const articles: Types.API.Article[] = await RedisUtil.Data.Articles.AllArticles.get(
			{
				version: context.version,
			},
		)

		// Map to exactly the fields rendered by ArticleCard so __NEXT_DATA__
		// stays small (draftJson/content/customStyles are not rendered here)
		const articleSummaries: ArticleSummary[] = articles.map((article) => ({
			title: article.title,
			slug: article.slug,
			liveJson: article.liveJson
				? {
						coverImage: article.liveJson.coverImage,
						subtitle: article.liveJson.subtitle,
						seoDescription: article.liveJson.seoDescription,
						tags: article.liveJson.tags,
						isFeaturedArticle: article.liveJson.isFeaturedArticle ?? false,
				  }
				: article.liveJson,
		}))

		return {
			props: {
				articles: articleSummaries,
				version: context?.version,
			},
		}
	},
)

export default ArticlesPage
